/**
 * The moneyline A/D hotkey contract — ONE focus card PER GAME.
 *
 * Approved product semantics (Live Trading V2 packet §V2B, confirmed against
 * the source review recording at 20:31–20:55): in a vertical two-team
 * moneyline layout,
 *
 *   A posts the visible top-of-book NO action for the visually TOP (away)
 *     team;
 *   D posts the visible top-of-book NO action for the visually BOTTOM
 *     (home) team.
 *
 * On Kalshi's separate per-team markets, the traders' money-making workflow
 * rests on the NO side of each named-team market. A therefore posts NO on the
 * top team's market and D posts NO on the bottom team's market. This is still
 * a per-GAME pair across two distinct tickers, never a YES/NO pair within one
 * team's market. Citadel explicitly clarified this missing side semantic in
 * the 2026-08-21 production acceptance pass.
 *
 * Boundaries:
 *   • No draw binding is invented: a soccer Draw row/band is MOUSE-ONLY and
 *     never registers or inherits an A/D card. A and D may still target the
 *     visibly top and bottom TEAMS of a 3-way game — that pairing stays
 *     unambiguous.
 *   • S (#724, opt-in) posts A's and D's joins together. It needs both
 *     prices to be bids (maker mode). On a 1X2 it posts both team NOs, as A
 *     then D would; the card says `threeWay` so the readout can say both pay
 *     on a draw and the losing-pair guard (two-outcome only) is skipped.
 *   • Each key is fail-closed on ITS OWN team's visibility: the away row
 *     carries the `data-lt2-price="a"` mark and the home row `"d"`, and the
 *     listener proves the pressed key's row is on screen before firing.
 *   • Spread/total hotkeys and every mouse one-click path are untouched.
 */
import { classifyLineAvailability } from '@/lib/sport/lineAvailability';
import { deriveAttackTarget } from '@/lib/trading/attackTarget';
import type { Lt2Period, Lt2Surface } from '@/lib/trading/liveIntent';
import type { Lt2CardTargets, Lt2HotkeyTarget } from '@/stores/lt2FocusStore';
import { pillPriceCents } from './ladder/helpers';
import { MULTI_OUTCOME_SPORTS, type GameRow, type PriceMode, type VenueBook } from './types';

export interface Lt2MoneylineCardScope {
  surface: Lt2Surface;
  period: Lt2Period;
  keyPrefix: string;
}

/** The Value Dashboard's standard moneyline board (main board only — the
 *  Saved board would double-register a game that is also on the slate). */
export const LT2_DASH_ML_CARD_SCOPE: Lt2MoneylineCardScope = {
  surface: 'dash-moneyline',
  period: 'full',
  keyPrefix: 'ml',
};

/** GameDetail's Moneyline section, detailed and compact modes. */
export const LT2_GD_ML_CARD_SCOPE: Lt2MoneylineCardScope = {
  surface: 'gd-moneyline',
  period: 'full',
  keyPrefix: 'gdml',
};

/**
 * The ONE place a moneyline hotkey-focus card id is built. GAME-scoped: the
 * id encodes surface prefix and game identity only — no outcome, side, or
 * ticker — so the A/D pair can never split across rows and a re-listed
 * market submits the ticker the resolver reads at keypress time.
 */
export function lt2MoneylineGameCardId(keyPrefix: string, gameKey: string): string {
  return `${keyPrefix}:${gameKey}`;
}

export interface Lt2MoneylineTeamInput {
  book: VenueBook | null;
  /** Short team identity for labels ("NYJ", "Arsenal"). */
  label: string;
  /** Fee-display policy hid this team's price — unavailable to the keyboard
   *  exactly as it is to the mouse. */
  priceHidden: boolean;
}

/**
 * Resolve the game's A/D pair from what the two team rows are displaying
 * RIGHT NOW. A = the top/away team's NO; D = the bottom/home team's NO.
 * `postOnly: false` mirrors the moneyline pills' own click semantics — the
 * hotkey contract is "the same value a mouse click there would submit".
 * `joinsBid` is the separate fact S needs: in maker mode the displayed NO
 * price is that market's best NO bid, so a post-only order at it rests.
 */
export function lt2MoneylineGameTargets(args: {
  scope: Lt2MoneylineCardScope;
  /** The focused game itself — the Pull All scope anchor. */
  game: GameRow;
  away: Lt2MoneylineTeamInput;
  home: Lt2MoneylineTeamInput;
  priceMode: PriceMode;
}): Lt2CardTargets {
  const { scope, game, away, home, priceMode } = args;
  const targetFor = (team: Lt2MoneylineTeamInput): Lt2HotkeyTarget => {
    const priceCents = team.priceHidden
      ? null
      : pillPriceCents(team.book, 'no', priceMode);
    return {
      book: team.book,
      side: 'no',
      priceCents,
      postOnly: false,
      joinsBid: priceCents != null && priceMode === 'maker',
      label: `${team.label} NO${priceCents != null ? ` @ ${priceCents}¢` : ''}`,
      availability: classifyLineAvailability(team.book),
      // W (top team) / E (bottom team) attack one level above this same
      // NO side's displayed best bid — the aggressive sibling of the same
      // action A/D post passively.
      attack: deriveAttackTarget(team.book, 'no', team.priceHidden),
    };
  };
  return {
    a: targetFor(away),
    d: targetFor(home),
    surface: scope.surface,
    marketKind: 'moneyline',
    period: scope.period,
    line: null,
    cardLabel: `${away.label}/${home.label} ML`,
    pullScope: { game },
    pausedReason: null,
    // A soccer 1X2 headline is the only multi-outcome pair a moneyline card
    // is built on; its knockout "To Advance" replacement is a true two-way
    // and carries the promotion evidence. Anything else from a multi-outcome
    // sport is treated as three-way (the readout names the draw exposure).
    threeWay: MULTI_OUTCOME_SPORTS.has(game.sport) && game.advancePromotion == null,
  };
}
